import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Search, SortAsc, SortDesc, RotateCcw, Eye, ExternalLink, Trash2, Plus, Sheet, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getReturnPathAfterSheetsConnect, isCampaignSheetsContext } from '@/lib/googleSheetsFlow';
import PageTutorialButton from '@/components/common/PageTutorialButton';
import GoogleSheetsPicker from './GoogleSheetsPicker';
import {
  getConnectedSheets,
  getGoogleSheetsAuthStatus,
  getAvailableSheets,
  connectGoogleSheet,
  createGoogleSheet,
  refreshAvailableGoogleSheets,
  getGoogleSheetsAuthorizationUrl,
} from '@/lib/googleSheetsApi';

// Import types
import {
  ConnectedSheet,
  AvailableSheet,
  SheetsPagination,
  SheetDataLoadResponse,
} from '@/types';

const GoogleSheetsIntegration: React.FC = () => {
  const { user, token } = useAuth();
  const userId = user?.id;
  const navigate = useNavigate();
  const location = useLocation();

  // State with proper TypeScript typing
  const [sheets, setSheets] = useState<ConnectedSheet[]>([]);
  const [, setAvailableSheets] = useState<AvailableSheet[]>([]);
  const [pagination, setPagination] = useState<SheetsPagination>({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [, setConnectedCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFetchingSheets, setIsFetchingSheets] = useState<boolean>(true);
  const [loadingSheetData, setLoadingSheetData] = useState<boolean>(false);
  const [syncingNames, setSyncingNames] = useState<boolean>(false);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [sheetData, setSheetData] = useState<SheetDataLoadResponse | null>(null);
  const [autoOpenPicker, setAutoOpenPicker] = useState<boolean>(false);
  const [showCreateSheetModal, setShowCreateSheetModal] = useState<boolean>(false);
  const [newSheetName, setNewSheetName] = useState<string>('');
  const [creatingSheet, setCreatingSheet] = useState<boolean>(false);
  const [googleAccountEmail, setGoogleAccountEmail] = useState<string | null>(null);
  const [campaignSheetsContext, setCampaignSheetsContext] = useState<string | null>(null);

  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 6;

  const oauthUrlHandledRef = useRef(false);
  const campaignContextToastShownRef = useRef(false);

  const loadUserSheets = useCallback(async (force = false): Promise<void> => {
    if (!userId || !token) {
      return;
    }

    try {
      console.log('Loading connected sheets for user:', user?.email);
      const data = await getConnectedSheets({ force });
      setSheets(data);
    } catch (error: any) {
      console.error('Error loading connected sheets:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a las hojas. Inicia sesión nuevamente.');
      } else {
        toast.error('Error al cargar las hojas conectadas');
      }
    } finally {
      setIsFetchingSheets(false);
    }
  }, [userId, token, user?.email]);

  const loadAvailableSheets = useCallback(async (page = 1, force = false): Promise<void> => {
    if (!userId || !token) {
      console.log('No user or token available');
      return;
    }

    try {
      setLoading(true);
      console.log(`Loading available sheets for user: ${user?.email}, page: ${page}`);
      const data = await getAvailableSheets(page, 6, { force });

      setAvailableSheets(data.sheets || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 6,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      });
      setConnectedCount(data.connected_count || 0);

    } catch (error: any) {
      console.error('Error loading available sheets:', error);
      // Don't show error toast here as it might be frequent if not auth'd
    } finally {
      setLoading(false);
    }
  }, [userId, token, user?.email]);

  const loadGoogleAccountInfo = useCallback(async (): Promise<void> => {
    if (!token) {
      setGoogleAccountEmail(null);
      return;
    }

    try {
      const response = await axios.get('/google-sheets/account-info', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setGoogleAccountEmail(response.data.email || null);
    } catch (error) {
      console.error('Error loading Google account info:', error);
      setGoogleAccountEmail(null);
    }
  }, [token]);

  // Initial load: only connected sheets (cards). Skip auth-status/available until Agregar Hoja.
  useEffect(() => {
    if (!userId || !token) return;

    void loadUserSheets();
    void loadGoogleAccountInfo();
  }, [userId, token, loadUserSheets, loadGoogleAccountInfo]);

  // OAuth / context URL handling (once per param value)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authStatus = params.get('auth');
    const context = params.get('context');

    if (authStatus === 'success') {
      if (!oauthUrlHandledRef.current) {
        oauthUrlHandledRef.current = true;
        toast.success('Conexión completada exitosamente');
        navigate(location.pathname, { replace: true });

        setTimeout(() => {
          void loadUserSheets(true);
          void loadAvailableSheets(1, true);

          if (isCampaignSheetsContext(context)) {
            setCampaignSheetsContext(context);
          }

          console.log('📂 OAuth completed from URL, opening Google Sheets Picker...');
          setAutoOpenPicker(true);
        }, 500);
      }
    } else if (context === 'campaign-builder' && !authStatus) {
      if (!campaignContextToastShownRef.current) {
        campaignContextToastShownRef.current = true;
        toast.info('Selecciona una hoja para continuar con tu campaña');
      }
    }
  }, [location.search, location.pathname, navigate, loadUserSheets, loadAvailableSheets]);

  // Listen for successful Google OAuth authentication from popup
  useEffect(() => {
    const handleGoogleOAuthMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type !== 'google_oauth_success') {
        return;
      }

      const context = event.data?.context || 'integrations';

      console.log('Google OAuth success received:', { context });
      
      void loadGoogleAccountInfo();

      if (isCampaignSheetsContext(context)) {
        setCampaignSheetsContext(context);
      }

      console.log('📂 OAuth completed, opening Google Sheets Picker...');
      setAutoOpenPicker(true);
    };

    window.addEventListener('message', handleGoogleOAuthMessage);

    return () => {
      window.removeEventListener('message', handleGoogleOAuthMessage);
    };
  }, [navigate, loadGoogleAccountInfo]);

  const disconnectSheet = async (sheetId: string): Promise<void> => {
    // Show confirmation toast or modal could be better, but keeping it simple for now
    try {
      await axios.post(`/google-sheets/${sheetId}/disconnect`);
      toast.success('Hoja desconectada exitosamente');
      // Reload lists
      void loadUserSheets(true);
      void loadAvailableSheets(pagination.page, true);

      // Clear data if we were viewing this sheet
      if (selectedSheet === sheetId) {
        setSheetData(null);
        setSelectedSheet(null);
      }
    } catch (error: any) {
      console.error('Error disconnecting sheet:', error);
      toast.error('Error al desconectar la hoja');
    }
  };

  const handlePickerSheetsSelected = async (
    selectedSheets: Array<{ id: string }>
  ): Promise<void> => {
    if (!token || selectedSheets.length === 0) {
      return;
    }

    setLoading(true);

    try {
      const connectedSheetIds = new Set(
        sheets.map((sheet) => sheet.sheet_id)
      );

      const sheetsToConnect = selectedSheets.filter(
        (sheet) => !connectedSheetIds.has(sheet.id)
      );

      const alreadyConnectedCount =
        selectedSheets.length - sheetsToConnect.length;

      if (sheetsToConnect.length === 0) {
        toast.info('Las hojas seleccionadas ya están conectadas');
        setAutoOpenPicker(false);

        if (campaignSheetsContext) {
          navigate(getReturnPathAfterSheetsConnect(campaignSheetsContext));
          setCampaignSheetsContext(null);
        }

        return;
      }

      let failedConnections = 0;

      for (const sheet of sheetsToConnect) {
        try {
          await connectGoogleSheet(sheet.id);
        } catch (error) {
          console.error(`Error connecting sheet ${sheet.id}:`, error);
          failedConnections += 1;
        }
      }

      await loadUserSheets(true);
      setAutoOpenPicker(false);

      if (alreadyConnectedCount > 0) {
        toast.info(
          `${alreadyConnectedCount} hoja(s) seleccionada(s) ya estaban conectadas`
        );
      }

      if (failedConnections > 0) {
        toast.error(
          `${failedConnections} hoja(s) no se pudieron conectar`
        );
      }

      if (
        campaignSheetsContext &&
        sheetsToConnect.length - failedConnections > 0
      ) {
        navigate(getReturnPathAfterSheetsConnect(campaignSheetsContext));
        setCampaignSheetsContext(null);
      }
    } finally {
      setLoading(false);
    }
  };
   
  const createNewSheet = async (): Promise<void> => {
    const trimmedName = newSheetName.trim();

    if (!token) {
      toast.error('No estás autenticado');
      return;
    }

    if (!trimmedName) {
      toast.error('Ingresa un nombre para la hoja');
      return;
    }

    setCreatingSheet(true);

    try {
      const createdSheet = await createGoogleSheet(trimmedName);

      toast.success(`Hoja "${createdSheet.name}" creada exitosamente`);

      await loadUserSheets(true);

      setNewSheetName('');
      setShowCreateSheetModal(false);
    } catch (error: any) {
      console.error('Error creating sheet:', error);
      toast.error(
        error.response?.data?.detail ||
        'Error al crear la hoja. Intenta nuevamente.'
      );
    } finally {
      setCreatingSheet(false);
    }
  };

  const handleGoogleAuth = async (): Promise<void> => {
    console.log('🚀 handleGoogleAuth called');
    setLoading(true);
    try {
      // First, check if user already has valid tokens
      console.log('🔍 Checking existing Google authentication...');
      const authStatus = await getGoogleSheetsAuthStatus({ force: true });

      console.log('📊 Auth status response:', authStatus);

      if (authStatus.is_authenticated && !authStatus.needs_auth) {
        // User is already authenticated, refresh sheets from Google Drive FIRST
        console.log('✅ User already authenticated, refreshing sheets from Google Drive...');
        toast.info('Actualizando hojas desde Google Drive...', { duration: 2000 });

        try {
          await refreshAvailableGoogleSheets();
          console.log('✅ Refreshed sheets from Google Drive');
          toast.success('Hojas actualizadas desde Google Drive');

        } catch (refreshError: any) {
          console.error('❌ Error refreshing sheets:', refreshError);
          if (refreshError.response?.status === 401) {
            toast.error('Tu sesión de Google expiró. Por favor reconecta tu cuenta.');
            // Fall back to OAuth flow
            await startOAuthFlow();
            setLoading(false);
            return;
          }
          // Continue even if refresh fails, sheets might still be in cache
          toast.warning('Error al actualizar hojas, mostrando las disponibles en caché');
        }

        // Abrir el Picker directamente sin pasar por la pantalla intermedia
        console.log('📂 Opening Google Sheets Picker directly...');
        setAutoOpenPicker(true);

      } else {
        // User needs to authenticate, start OAuth flow
        console.log('🔐 User needs authentication, starting OAuth flow...');
        await startOAuthFlow();
      }

    } catch (error: any) {
      console.error('Error checking auth status:', error);
      toast.error('Error verificando autenticación. Iniciando proceso completo...');
      // Fall back to OAuth flow on error
      await startOAuthFlow();
    } finally {
      setLoading(false);
    }
  };

 const startOAuthFlow = async (): Promise<void> => {
  try {
    console.log('🔐 Starting OAuth flow...');

    // Obtener contexto de los parámetros de la URL
    const params = new URLSearchParams(location.search);
    const context = params.get('context');

    // Usar 'integrations' por defecto si no hay contexto
    const authorizationUrl = await getGoogleSheetsAuthorizationUrl(context || 'integrations');
    if (!authorizationUrl) throw new Error('No authorization URL received');

    console.log('📝 OAuth URL received, opening popup...');

    // Tamaño y posición del popup
    const width = 500;
    const height = 650;

    const left =
      window.screenX + (window.outerWidth - width) / 2;

    const top =
      window.screenY + (window.outerHeight - height) / 2;

    // Abrir OAuth de Google en una ventana adicional
    const popup = window.open(
      authorizationUrl,
      'google_oauth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      console.error('❌ Google OAuth popup was blocked');
      toast.error(
        'No se pudo abrir la ventana de Google. Verifica que los popups estén permitidos.'
      );
      setLoading(false);
      return;
    }

    popup.focus();

  } catch (error: any) {
    console.error('Error starting OAuth flow:', error);
    toast.error('Error al iniciar la autenticación con Google');
    setLoading(false);
    throw error;
  }
};

  const oauthRetryHandledRef = useRef(false);

  // Handle auth=retry after startOAuthFlow is defined
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('auth') !== 'retry') return;
    if (oauthRetryHandledRef.current) return;
    oauthRetryHandledRef.current = true;
    navigate(location.pathname, { replace: true });
    void startOAuthFlow();
  }, [location.search]);

  const syncSheetNames = async (): Promise<void> => {
    if (!userId || !token) {
      return;
    }

    setSyncingNames(true);
    try {
      const response = await axios.post(`/google-sheets/sync-names`);
      const updated = response.data.updated || 0;

      if (updated > 0) {
        toast.success(`Se actualizaron ${updated} hoja(s) desde Google Drive`);
        await loadUserSheets(true);
      } else {
        toast.info('Todas las hojas ya están actualizadas');
      }
    } catch (error: any) {
      console.error('Error syncing sheet names:', error);
      toast.error(error.response?.data?.detail || 'Error al sincronizar nombres de hojas');
    } finally {
      setSyncingNames(false);
    }
  };

  const resetGoogleAuth = async (): Promise<void> => {
    try {
      await axios.delete(`/google-sheets/reset-auth`);
      toast.success('Google Sheets desconectado. Google Calendar no se modificó.');

      // Clear data from the previous Google account
      setSheets([]);
      setGoogleAccountEmail(null);
      setSheetData(null);
      setSelectedSheet(null);
      setCurrentPage(1);
      // Trigger re-auth
      setTimeout(() => {
        handleGoogleAuth();
      }, 1000);
    } catch (error: any) {
      console.error('Error resetting auth:', error);
      toast.error('Error al resetear la autenticación');
    }
  };

  const loadSheetData = async (sheetId: string): Promise<void> => {
    console.log('=== loadSheetData called ===');

    if (!token) {
      console.error('No token available');
      return;
    }

    if (!sheetId) {
      console.error('No sheetId provided');
      return;
    }

    setLoadingSheetData(true);
    try {
      console.log('Making API request...');
      const response = await axios.get<SheetDataLoadResponse>(
        `/google-sheets/${sheetId}/data?max_rows=8`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('API Response received:', response);

      if (response.data && response.data.columns && response.data.rows) {
        // Asegurar que tenemos el formato correcto
        const formattedData: SheetDataLoadResponse = {
          ...response.data,
          columns: Array.isArray(response.data.columns) ? response.data.columns : [],
          rows: Array.isArray(response.data.rows) ? response.data.rows : []
        };

        setSheetData(formattedData);
        setSelectedSheet(sheetId);

        console.log('Sheet data formatted and stored:', formattedData);

        toast.success(`Datos cargados: ${formattedData.columns.length} columnas, ${formattedData.rows.length} filas`);

        // Scroll to data viewer
        setTimeout(() => {
          document.getElementById('sheet-data-viewer')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else if (response.data && response.data.error) {
        console.error('API returned error:', response.data.error);
        toast.error(`Error del servidor: ${response.data.error}`);
      } else {
        console.error('Invalid data format received:', response.data);
        throw new Error('Formato de datos inválido recibido del servidor');
      }
    } catch (error: any) {
      console.error('=== Error in loadSheetData ===');
      const errorMsg = error.response?.data?.detail || error.message || 'Error al cargar los datos de la hoja';
      toast.error(errorMsg);
    } finally {
      setLoadingSheetData(false);
    }
  };

  // Helper for filtering/sorting
  const getFilteredSheets = () => {
    let filtered = [...sheets];

    // Search
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(sheet =>
        sheet.name.toLowerCase().includes(lowerTerm) ||
        sheet.sheet_id.toLowerCase().includes(lowerTerm)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortOrder === 'asc') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    return filtered;
  };

  const filteredSheets = getFilteredSheets();
  const totalPages = Math.max(1, Math.ceil(filteredSheets.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSheets = filteredSheets.slice(startIndex, endIndex);

  // Helper to get random stats for UI demo if real data not available
  const getSheetStats = (sheet: ConnectedSheet) => {
    return {
      // @ts-ignore
      usage: sheet.campaign_usage || 0,
      lastSync: new Date(sheet.last_modified || sheet.created_at).toLocaleDateString()
    };
  };

  const SheetDataViewer: React.FC = () => {
    if (!sheetData) return null;

    if (!sheetData.columns || sheetData.columns.length === 0) {
      return (
        <Card className="mt-8 border-gray-200" id="sheet-data-viewer">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Sheet className="h-5 w-5 text-[#1CB3B3]" />
              <span>Datos de la Hoja</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="text-gray-400 mb-4">
                <Sheet className="h-16 w-16 mx-auto text-gray-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos disponibles</h3>
              <p className="text-gray-500">La hoja de cálculo parece estar vacía o no tiene encabezados definidos.</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="mt-8 border-gray-200 shadow-md animate-fadeIn" id="sheet-data-viewer">
        <CardHeader className="bg-gradient-to-r from-[#f0f9fa] to-[#e6f7f4] border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center space-x-2 text-xl">
                <Sheet className="h-5 w-5 text-[#1CB3B3]" />
                <span>Vista Previa de Datos</span>
              </CardTitle>
              <CardDescription className="mt-1">
                Visualizando {sheetData.rows.length} filas de la hoja &quot;{sheetData.sheet_name || 'Seleccionada'}&quot;
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => loadSheetData(selectedSheet!)} disabled={loadingSheetData} className="bg-white">
                <RotateCcw className={`h-4 w-4 mr-2 ${loadingSheetData ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Columnas Detectadas</h4>
              <div className="flex flex-wrap gap-2">
                {sheetData.columns.map((column, index) => (
                  <Badge key={index} variant="secondary" className="bg-[#e6f7f4] text-[#1CB3B3] border-[#1CB3B3]/20 px-3 py-1">
                    {column}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                    <tr>
                      {sheetData.columns.map((column, index) => (
                        <th key={index} className="px-6 py-3 font-semibold whitespace-nowrap">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sheetData.rows.slice(0, 8).map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50 transition-colors">
                        {sheetData.columns.map((column, colIndex) => (
                          <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-gray-900">
                            {Array.isArray(row) ? (row[colIndex] || '-') : ((row as any)[column] || '-')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 px-6 py-3 border-t text-xs text-gray-500 flex justify-between items-center">
                <span>Mostrando {Math.min(8, sheetData.rows.length)} filas de {sheetData.total_rows_fetched || sheetData.rows.length}</span>
              </div>
            </div>

          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between space-y-4 xl:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Google Sheets</h1>
            <p className="text-gray-600 mt-1">
              Gestiona y conecta tus hojas de cálculo para campañas
            </p>

            {googleAccountEmail && (
              <p className="text-sm font-medium text-gray-700 mt-1 break-all">
                {googleAccountEmail}
              </p>
            )}
            </div>
          <div className="flex flex-wrap items-center justify-start gap-2 w-full xl:flex-nowrap xl:w-auto xl:justify-end">
            <PageTutorialButton tutorialKey="google_sheets" />

            {/* ✅ NUEVO: Botón especial para contexto de campaign-builder */}
            {(() => {
              const params = new URLSearchParams(location.search);
              const context = params.get('context');

              if (context === 'campaign-builder' && sheets.length > 0) {
                return (
                  <Button
                    onClick={() => navigate(getReturnPathAfterSheetsConnect(context))}
                    className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-6 h-11 rounded-xl shadow-lg flex items-center gap-2 font-semibold transition-all active:scale-95"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2 rotate-180" />
                    Continuar con Campaña
                  </Button>
                );
              }

              return null;
            })()}

            {sheets.length > 0 && (
              <>
                <Button
                  onClick={syncSheetNames}
                  disabled={syncingNames}
                  variant="outline"
                  className="bg-white hover:bg-slate-50 border-gray-200 text-gray-500 w-11 h-11 p-0 rounded-xl shadow-sm transition-all active:scale-95 group"
                >
                  <RotateCcw className={`w-5 h-5 transition-transform duration-500 ${syncingNames ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                </Button>
                <Button
                  onClick={resetGoogleAuth}
                  variant="outline"
                  className="shrink-0 bg-white hover:bg-slate-50 border-gray-200 text-gray-500 px-3 sm:px-4 h-11 rounded-xl shadow-sm font-semibold transition-all active:scale-95"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Cambiar Cuenta
                </Button>
              </>
            )}

            {sheets.length > 0 || autoOpenPicker || googleAccountEmail ? (
              <>
                <GoogleSheetsPicker
                  onSheetsSelected={handlePickerSheetsSelected}
                  onPickerClosed={() => setAutoOpenPicker(false)}
                  autoOpen={autoOpenPicker}
                  buttonText="Conectar Hojas"
                  disabled={loading}
                  className="w-full sm:w-auto bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-4 sm:px-6 h-11 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95"
                />

                <Button
                  onClick={() => setShowCreateSheetModal(true)}
                  disabled={creatingSheet}
                  className="w-full sm:w-auto bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-4 sm:px-6 h-11 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95"
                >
                  <Plus className="w-5 h-5" />
                  Crear Hoja Nueva
                </Button>
              </>
            ) : (
              <Button
                onClick={handleGoogleAuth}
                disabled={loading}
                className="w-full sm:w-auto bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-4 sm:px-6 h-11 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Conectando...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Nueva Conexión
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {showCreateSheetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-5">
                <h2 className="text-xl font-bold text-gray-900">
                  Nueva Hoja de Cálculo
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Ingresa el nombre de la nueva hoja de cálculo.
                </p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void createNewSheet();
                }}
              >
                <div className="mb-6">
                  <label
                    htmlFor="new-sheet-name"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Nombre de la hoja
                  </label>

                  <input
                    id="new-sheet-name"
                    type="text"
                    value={newSheetName}
                    onChange={(e) => setNewSheetName(e.target.value)}
                    placeholder="Ej. Clientes octubre 2026"
                    autoFocus
                    disabled={creatingSheet}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 outline-none transition focus:border-[#1CB3B3] focus:ring-2 focus:ring-[#1CB3B3]/20 disabled:bg-gray-100"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={creatingSheet}
                    onClick={() => {
                      setNewSheetName('');
                      setShowCreateSheetModal(false);
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    type="submit"
                    disabled={!newSheetName.trim() || creatingSheet}
                    className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white"
                  >
                    {creatingSheet ? (
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Creando...
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        Crear Hoja
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <Card className="border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Buscar hoja por nombre..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-transparent transition-all"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center space-x-2 min-w-[140px] justify-between"
                >
                  <div className="flex items-center space-x-2">
                    {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                    <span>{sortOrder === 'asc' ? 'Más antiguas' : 'Más recientes'}</span>
                  </div>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Area */}
        {isFetchingSheets ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                      <div className="w-32 h-6 bg-gray-200 rounded"></div>
                    </div>
                    <div className="w-20 h-6 bg-gray-200 rounded-full"></div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-3">
                      <div className="h-14 bg-gray-100 rounded-lg"></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <div className="h-10 bg-gray-100 rounded-lg"></div>
                      <div className="h-10 bg-gray-100 rounded-lg"></div>
                      <div className="col-span-2 h-10 bg-gray-100 rounded-lg mt-1"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : sheets.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-200 bg-gray-50/50">
            <CardContent className="pt-12 pb-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#e6f7f4] to-[#f0f9fa] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#e0f2f1]">
                  <Sheet className="h-8 w-8 text-[#1CB3B3]" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  Conecta Google Sheets
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Conecta tus hojas de cálculo de Google Drive para importar contactos y utilizarlos en tus campañas de WhatsApp masivas.
                </p>
                <div className="flex justify-center flex-col sm:flex-row gap-4">
                  <Button
                    onClick={handleGoogleAuth}
                    className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-8 h-11 rounded-xl shadow-lg flex items-center gap-2 font-semibold transition-all active:scale-95"
                  >
                    <Plus className="w-5 h-5" />
                    Conectar Google Drive
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Sheets Grid */}
           <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {paginatedSheets.map((sheet) => {
                const stats = getSheetStats(sheet);
                const isSelected = selectedSheet === sheet.sheet_id;

                return (
                  <Card
                    key={sheet.sheet_id}
                    className={`hover:shadow-lg transition-all duration-300 border-gray-200 group ${isSelected ? 'ring-2 ring-[#1CB3B3] shadow-md' : ''}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 overflow-hidden">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#e6f7f4] to-[#f0f9fa] border border-[#e0f2f1] rounded-lg flex items-center justify-center flex-shrink-0">
                            <Sheet className="h-5 w-5 text-[#1CB3B3]" />
                          </div>
                          <div className="overflow-hidden">
                            <CardTitle className="text-lg truncate" title={sheet.name}>
                              {sheet.name}
                            </CardTitle>
                          </div>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent>
                      <div className="space-y-4">
                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 gap-3 text-center">
                          <div className="border border-gray-100 rounded-lg p-2 bg-gray-50">
                            <div className="text-xs text-gray-500 mb-1">Última Modificación</div>
                            <div className="font-semibold text-gray-900 text-sm">
                              {stats.lastSync}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-2 gap-2 pt-2">
                          <Button
                            size="sm"
                            className="h-10 px-3 flex items-center justify-center rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 border bg-[#E8F7F7] text-[#0D7377] border-[#1CB3B3]/30 hover:bg-[#D5F0F0] hover:border-[#1CB3B3]/50 shadow-sm"
                            onClick={() => loadSheetData(sheet.sheet_id)}
                          >
                            {isSelected && loadingSheetData ? (
                              <div className="spinner w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                            ) : (
                              <Eye className="h-4 w-4 mr-1.5" />
                            )}
                            Ver Datos
                          </Button>
                          <a
                            href={sheet.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium border bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:border-slate-300 shadow-sm rounded-md transition-colors"
                          >
                            <ExternalLink className="h-4 w-4 mr-1.5" /> Abrir
                          </a>
                          <Button
                            variant="outline"
                            size="sm"
                            className="col-span-2 bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100 hover:border-rose-300 hover:text-rose-700 shadow-sm transition-all"
                            onClick={() => disconnectSheet(sheet.sheet_id)}
                          >
                            <Trash2 className="h-4 w-4 mr-1.5" /> Desconectar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Pagination - Styled to match MessageTemplates */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-6">
                <div className="text-sm text-gray-600">
                  Página {currentPage} de {totalPages} — mostrando {paginatedSheets.length} de {filteredSheets.length} hojas
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>

                  <div className="hidden sm:flex items-center gap-1">
                    {Array.from({ length: totalPages }).map((_, i) => {
                      const page = i + 1;
                      const isActive = page === currentPage;
                      // Mostrar máximo páginas inteligentemente con "..."
                      if (totalPages <= 7 || page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 rounded-md text-sm transition-colors ${isActive
                              ? 'bg-[#1CB3B3] text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-1 text-gray-400">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Data Viewer Section */}
        <SheetDataViewer />
      </div>
    </div>
  );
};

export default GoogleSheetsIntegration;